import { Fragment, useState, type ReactNode } from "react";
import {
  type InteractionEvent,
  type EventType,
  type EventOption,
} from "../../types";
import { COMMON_EVENT_OPTIONS } from "../../config/defaultEventOptions";
import { keyEventOptions, findEventOptionByKey, nextQuickActions, categoryOf } from "../../utils/eventOptionSelection";
import { Timestamp } from "firebase/firestore";
import { editedStepZone, zoneAbbrev } from "../../utils/tripDisplay";
import { fromZonedInputValue, toZonedInputValue } from "../../utils/zonedTime";
import { deepEqual } from "../../utils/editDiff";

interface EventTimelineProps {
  events: InteractionEvent[];
  onAddEvent: (event: { eventType: EventType; notes: string; customLabel?: string }) => Promise<void>;
  /**
   * Remove / edit one event. Handed the event itself, exactly as it came in
   * `events`, never a position: another phone or a watch can add, edit or
   * remove events while an edit is open, and a position captured when it
   * opened would then point at someone else's step.
   */
  onRemoveEvent: (event: InteractionEvent) => Promise<void>;
  onUpdateEvent: (event: InteractionEvent, patch: { eventTimestamp?: Timestamp; notes?: string | null; customLabel?: string | null; timezone?: string | null }) => Promise<void>;
  entityEventOptions?: EventOption[];
  /**
   * Zone the activity ran on. Each event reads on the clock it was logged on
   * (`event.timezone`); this covers events logged before they carried one.
   */
  zone?: string;
  /** A flight: its steps cross zones, so an edited step keeps its own. */
  isFlight?: boolean;
  /** An "Edit buttons" control to show beside the quick steps, if allowed. */
  editButtons?: ReactNode;
  /**
   * Show the next-step buttons. Off where the page already puts them on its
   * header card; the timeline then keeps just "+ Add a step" for the rest.
   */
  quickActions?: boolean;
}

function formatTime(ts: Timestamp, zone?: string): string {
  return ts.toDate().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: zone });
}

/** The clock one event was logged on. */
function zoneOf(event: InteractionEvent, fallback?: string): string | undefined {
  return event.timezone || fallback || undefined;
}

function eventLabel(event: InteractionEvent, eventOptions: EventOption[]): string {
  // Custom label takes priority
  if (event.customLabel) {
    return event.customLabel;
  }
  // Look up display name from provided event options
  const match = eventOptions.find((e) => e.eventType === event.eventType);
  if (match) return match.displayName;
  // Fallback: humanize the event type
  return event.eventType.replace(/_/g, " ");
}

const CUSTOM_EVENT_SENTINEL = "__CUSTOM__";


export function EventTimeline({ events, onAddEvent, onRemoveEvent, onUpdateEvent, entityEventOptions, zone, isFlight, editButtons, quickActions = true }: EventTimelineProps) {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [customLabel, setCustomLabel] = useState("");
  const [eventNotes, setEventNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [quickBusyId, setQuickBusyId] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState<InteractionEvent | null>(null);
  const [editTimeValue, setEditTimeValue] = useState("");
  const [editNotesValue, setEditNotesValue] = useState("");
  const [editLabelValue, setEditLabelValue] = useState("");
  const [updatingEvent, setUpdatingEvent] = useState<InteractionEvent | null>(null);
  const isSame = (a: InteractionEvent | null, b: InteractionEvent) => a != null && deepEqual(a, b);

  const options = entityEventOptions && entityEventOptions.length > 0
    ? entityEventOptions
    : COMMON_EVENT_OPTIONS;

  // Address options by key, never by event type: several options routinely
  // share one event type (an airport's "Security Line", "Bag Drop" and "Bag at
  // Carousel" are all PROGRESS_UPDATE), so a <select> whose values were event
  // types would collapse them into a single choice — picking any of them would
  // select and log the first one instead.
  const keyedOptions = keyEventOptions(options);

  const defaultSelection = keyedOptions[0]?.key ?? CUSTOM_EVENT_SENTINEL;

  // The entity's flow decides what goes on a button and in what order; the
  // full list stays one tap away under "More…". See `utils/activityFlow`.
  const quick = nextQuickActions(options, events);

  // Track original indices so mutations target the right event
  const sortedWithIndex = events
    .map((event, originalIndex) => ({ event, originalIndex }))
    .sort((a, b) => a.event.eventTimestamp.toMillis() - b.event.eventTimestamp.toMillis());

  // A flight boards on one clock and lands on another. When this activity's
  // events span more than one zone, every time names its zone so the gap
  // between them reads right; on an ordinary one-clock activity none do.
  const mixedZones = new Set(events.map((e) => zoneOf(e, zone) ?? "")).size > 1;
  const showTime = (event: InteractionEvent): string => {
    const z = zoneOf(event, zone);
    const time = formatTime(event.eventTimestamp, z);
    const abbrev = mixedZones ? zoneAbbrev(event.eventTimestamp.toDate(), z) : null;
    return abbrev ? `${time} ${abbrev}` : time;
  };

  const handleRemove = async (event: InteractionEvent) => {
    const label = eventLabel(event, options);
    const time = showTime(event);
    if (!confirm(`Remove "${label}" at ${time}?`)) return;
    setUpdatingEvent(event);
    try {
      await onRemoveEvent(event);
    } catch {
      // error handled upstream
    }
    setUpdatingEvent(null);
  };

  const handleStartEdit = (event: InteractionEvent) => {
    setEditingEvent(event);
    // Edited on the clock the event was logged on, not this device's.
    setEditTimeValue(toZonedInputValue(event.eventTimestamp.toDate(), zoneOf(event, zone)));
    setEditNotesValue(event.notes ?? "");
    setEditLabelValue(event.customLabel ?? "");
  };

  const handleSaveEdit = async () => {
    const orig = editingEvent;
    if (!orig) return;
    const newDate = fromZonedInputValue(editTimeValue, zoneOf(orig, zone));
    if (!newDate) return;
    // Only custom-labelled events are renameable; keep the old label rather than
    // letting it go blank (a blank would fall back to the humanized event type).
    const patch: { eventTimestamp?: Timestamp; notes?: string | null; customLabel?: string | null; timezone?: string | null } = {
      eventTimestamp: Timestamp.fromDate(newDate),
      notes: editNotesValue.trim() || null,
    };
    // A step logged late carries the phone's zone at the time of the tap;
    // moved back among the others, it takes theirs.
    if (patch.eventTimestamp!.toMillis() !== orig.eventTimestamp.toMillis()) {
      const z = editedStepZone(events, orig, newDate.getTime(), isFlight);
      if (z && z !== (orig.timezone ?? null)) patch.timezone = z;
    }
    if (orig.customLabel != null) {
      patch.customLabel = editLabelValue.trim() || orig.customLabel;
    }
    setUpdatingEvent(orig);
    try {
      await onUpdateEvent(orig, patch);
    } catch {
      // error handled upstream
    }
    setEditingEvent(null);
    setUpdatingEvent(null);
  };

  // Record the label of the step that was actually chosen. Event types don't
  // identify a step on their own (several options share PROGRESS_UPDATE), and
  // an entity's options can change later, so a stored label is the only way the
  // timeline can keep showing what was tapped.
  const labelFor = (opt: EventOption) => opt.displayName;

  const handleQuickAdd = async (opt: EventOption) => {
    if (quickBusyId) return;
    setQuickBusyId(opt.id);
    try {
      await onAddEvent({ eventType: opt.eventType, notes: "", customLabel: labelFor(opt) });
    } catch {
      // error handled upstream
    }
    setQuickBusyId(null);
  };

  const handleAdd = async () => {
    const key = selectedKey || defaultSelection;
    const isCustom = key === CUSTOM_EVENT_SENTINEL;
    if (isCustom && !customLabel.trim()) return;
    const picked = isCustom ? undefined : findEventOptionByKey(keyedOptions, key);
    if (!isCustom && !picked) return;

    setSaving(true);
    try {
      if (isCustom) {
        await onAddEvent({
          eventType: "PROGRESS_UPDATE",
          notes: eventNotes.trim(),
          customLabel: customLabel.trim(),
        });
      } else {
        await onAddEvent({
          eventType: picked!.eventType,
          notes: eventNotes.trim(),
          customLabel: labelFor(picked!),
        });
      }
      setEventNotes("");
      setCustomLabel("");
      setSelectedKey("");
      setShowPicker(false);
    } catch {
      // error handled upstream
    }
    setSaving(false);
  };

  // Group options by category for the dropdown
  const categoryLabels: Record<string, string> = {
    START: "Start",
    ACTIVE: "Active",
    END: "End",
    MILESTONE: "Milestone",
    DELAY_START: "Delay",
    DELAY_END: "Delay",
  };
  const groupKey = (opt: EventOption) => {
    const c = categoryOf(opt);
    return c === "DELAY_END" ? "DELAY_START" : c;
  };

  // Build grouped options, preserving order
  const groupedOptions: { label: string; items: typeof keyedOptions }[] = [];
  const seenCategories = new Set<string>();
  for (const { opt } of keyedOptions) {
    const catKey = groupKey(opt);
    if (!seenCategories.has(catKey)) {
      seenCategories.add(catKey);
      groupedOptions.push({
        label: categoryLabels[catKey] ?? catKey,
        items: keyedOptions.filter((k) => groupKey(k.opt) === catKey),
      });
    }
  }

  const openPicker = () => {
    setSelectedKey(selectedKey || defaultSelection);
    setShowPicker(true);
  };

  return (
    <div className="event-timeline">
      <div className="event-timeline-header">
        <h3>{quickActions ? "Events" : "Steps"}</h3>
        {!quickActions && !showPicker && (
          <button type="button" className="btn btn-small btn-secondary" onClick={openPicker}>
            + Add a step
          </button>
        )}
      </div>

      {quickActions && !showPicker && (
        <div className="event-quick-actions">
          {quick.options.map((opt, index) => (
            <Fragment key={opt.id}>
              {/* A queue can lead to the lockers or the preshow or straight
                  on; when the flow says so, say so, rather than letting the
                  second chip read as a lesser version of the first. */}
              {quick.branching && index === 1 && <span className="event-quick-or">or</span>}
              <button
                type="button"
                className={[
                  "btn btn-small event-quick-btn",
                  index === 0 && !quick.delayOpen ? "event-quick-btn--lead" : "",
                  categoryOf(opt) === "DELAY_START" || categoryOf(opt) === "DELAY_END"
                    ? "event-quick-btn--delay"
                    : "",
                ].filter(Boolean).join(" ")}
                onClick={() => handleQuickAdd(opt)}
                disabled={quickBusyId !== null}
              >
                {quickBusyId === opt.id ? "Adding…" : opt.displayName}
              </button>
            </Fragment>
          ))}
          <button
            type="button"
            className="btn btn-small btn-secondary event-quick-btn"
            onClick={openPicker}
          >
            {quick.options.length === 0 ? "+ Add Event" : "More…"}
          </button>
          {editButtons}
        </div>
      )}

      {showPicker && (
        <div className="event-add-form">
          <select
            className="form-input"
            value={selectedKey || defaultSelection}
            onChange={(e) => {
              setSelectedKey(e.target.value);
              if (e.target.value !== CUSTOM_EVENT_SENTINEL) {
                setCustomLabel("");
              }
            }}
          >
            {groupedOptions.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.items.map(({ key, opt }) => (
                  <option key={key} value={key}>{opt.displayName}</option>
                ))}
              </optgroup>
            ))}
            <optgroup label="Other">
              <option value={CUSTOM_EVENT_SENTINEL}>Custom Event...</option>
            </optgroup>
          </select>
          {(selectedKey === CUSTOM_EVENT_SENTINEL) && (
            <input
              type="text"
              className="form-input"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="Custom event name"
            />
          )}
          <input
            type="text"
            className="form-input"
            value={eventNotes}
            onChange={(e) => setEventNotes(e.target.value)}
            placeholder="Optional notes"
          />
          <div className="form-actions">
            <button type="button" className="btn btn-small" onClick={handleAdd} disabled={saving}>
              {saving ? "Adding..." : "Add"}
            </button>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => setShowPicker(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {sortedWithIndex.length === 0 ? (
        <p className="text-muted">No events recorded yet.</p>
      ) : (
        <ul className="event-list">
          {sortedWithIndex.map(({ event, originalIndex }) => {
            const isEditing = isSame(editingEvent, event);
            const isUpdating = isSame(updatingEvent, event);
            // Round-derived order events are a read-only mirror of a dining
            // round's timing — edit them via the round, not the timeline.
            const isRoundDerived = event.roundIndex != null;
            return (
            <li key={originalIndex} className={`event-item${isRoundDerived ? " event-item-derived" : ""}`}>
              <span className="event-time">
                {isRoundDerived ? (
                  <span className="event-time-static">{showTime(event)}</span>
                ) : isEditing ? (
                  <input
                    type="datetime-local"
                    step="1"
                    className="form-input event-time-input"
                    value={editTimeValue}
                    onChange={(e) => setEditTimeValue(e.target.value)}
                  />
                ) : (
                  <button
                    type="button"
                    className="btn-link event-time-btn"
                    title="Edit event"
                    onClick={() => handleStartEdit(event)}
                  >
                    {showTime(event)}
                  </button>
                )}
              </span>
              <span className="event-dot" />
              <div className="event-content">
                <span className="event-type">{eventLabel(event, options)}</span>
                {isRoundDerived ? (
                  <span className="event-derived-hint">from round</span>
                ) : isEditing ? (
                  <>
                    {event.customLabel != null && (
                      <input
                        type="text"
                        className="form-input event-notes-input"
                        value={editLabelValue}
                        onChange={(e) => setEditLabelValue(e.target.value)}
                        placeholder="Event name"
                        autoFocus
                      />
                    )}
                    <input
                      type="text"
                      className="form-input event-notes-input"
                      value={editNotesValue}
                      onChange={(e) => setEditNotesValue(e.target.value)}
                      placeholder="Add a note…"
                      autoFocus={event.customLabel == null}
                    />
                    <div className="event-edit-actions">
                      <button
                        type="button"
                        className="btn btn-small"
                        onClick={() => handleSaveEdit()}
                        disabled={isUpdating}
                      >
                        {isUpdating ? "Saving..." : "Save"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={() => setEditingEvent(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : event.notes ? (
                  <button
                    type="button"
                    className="event-notes event-notes-btn"
                    title="Edit note"
                    onClick={() => handleStartEdit(event)}
                  >
                    {event.notes}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="event-add-note-btn"
                    onClick={() => handleStartEdit(event)}
                  >
                    + note
                  </button>
                )}
              </div>
              {!isEditing && !isRoundDerived && (
                <button
                  type="button"
                  className="btn-icon event-remove-btn"
                  title="Remove event"
                  onClick={() => handleRemove(event)}
                  disabled={isUpdating}
                >
                  &times;
                </button>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
